import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createRequest, getMyVenueProfile } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { TimeField, TimeFieldGroup } from '../components/ui/time-field';
import { Send, AlertCircle, Clock, Euro, Settings, Briefcase, Music, MapPin, X, Plus, Users, Coffee, Cloud, Phone, Car, Truck } from 'lucide-react';
import { toast } from 'sonner';

export default function CreateRequestPage() {
  const navigate = useNavigate();
  const [venueProfile, setVenueProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Basic request fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [city, setCity] = useState('');
  const [venueAddress, setVenueAddress] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressRef = React.useRef(null);
  const [eventDate, setEventDate] = useState('');
  const [startTime, setStartTime] = useState('20:00');
  const [endTime, setEndTime] = useState('23:00');
  const [roleTypeNeeded, setRoleTypeNeeded] = useState('MUSICIAN');
  const [roleSubtype, setRoleSubtype] = useState('');
  const [budgetAmount, setBudgetAmount] = useState('');
  const [rateType, setRateType] = useState('');
  const [urgency, setUrgency] = useState('FLEXIBLE');
  const [expectedAttendance, setExpectedAttendance] = useState('');

  // Booking Terms - Call Time
  const [arrivalTime, setArrivalTime] = useState('');
  const [hardStop, setHardStop] = useState('Flexible');

  // Booking Terms - Pay
  const [payRateType, setPayRateType] = useState('Hourly');
  const [bookingCovers, setBookingCovers] = useState('');
  const [beyondThat, setBeyondThat] = useState('Same rate applies');

  // Booking Terms - Prep & Soundcheck
  const [soundcheckIncluded, setSoundcheckIncluded] = useState(true);
  const [soundcheckDuration, setSoundcheckDuration] = useState('30');
  const [setupPrepTime, setSetupPrepTime] = useState('Unpaid');

  // Booking Terms - Downtime Policy
  const [downtimeStatus, setDowntimeStatus] = useState('Paid');
  const [mobility, setMobility] = useState('');

  // Booking Terms - Role Scope
  const [primaryRole, setPrimaryRole] = useState('');
  const [roleIncludes, setRoleIncludes] = useState([]);
  const [roleExcludes, setRoleExcludes] = useState([]);
  const [newInclude, setNewInclude] = useState('');
  const [newExclude, setNewExclude] = useState('');

  // Booking Terms - Equipment
  const [venueProvides, setVenueProvides] = useState('');
  const [talentBrings, setTalentBrings] = useState([]);
  const [optionalItems, setOptionalItems] = useState([]);
  const [equipmentFailurePolicy, setEquipmentFailurePolicy] = useState('Talent responsible for own gear');
  const [newTalentBring, setNewTalentBring] = useState('');
  const [newOptional, setNewOptional] = useState('');

  // Booking Terms - Travel
  const [includedRadiusKm, setIncludedRadiusKm] = useState('');
  const [beyondRadiusRate, setBeyondRadiusRate] = useState('');
  const [parkingCovered, setParkingCovered] = useState(false);
  const [tollsCovered, setTollsCovered] = useState(false);

  // Role & Execution (performance shape)
  const [execSetsCount, setExecSetsCount] = useState(2);
  const [execSetLength, setExecSetLength] = useState('45');
  const [execBreakLength, setExecBreakLength] = useState('15');
  const [execPerformanceMode, setExecPerformanceMode] = useState('');
  const [execRoleType, setExecRoleType] = useState('');
  const [execBillPosition, setExecBillPosition] = useState('');
  const [execMaterialSource, setExecMaterialSource] = useState('');
  const [execAnnouncementLanguage, setExecAnnouncementLanguage] = useState('');
  const [execNotes, setExecNotes] = useState('');
  const [execErrors, setExecErrors] = useState({});

  // Hospitality
  const [hospFood, setHospFood] = useState('');
  const [hospFoodDetail, setHospFoodDetail] = useState('');
  const [hospDressingRoom, setHospDressingRoom] = useState('');
  const [hospSecureStorage, setHospSecureStorage] = useState('');
  const [hospWifi, setHospWifi] = useState('');
  const [hospDrinks, setHospDrinks] = useState('');
  const [hospShower, setHospShower] = useState('');
  const [hospGreenRoom, setHospGreenRoom] = useState('');
  const [hospErrors, setHospErrors] = useState({});
  // Catering — optional; empty `provided` means the request carries no catering at all
  const [cateringProvided, setCateringProvided] = useState('');
  const [cateringServedAt, setCateringServedAt] = useState('');
  const [cateringVegetarian, setCateringVegetarian] = useState('');

  // Event Environment
  const [envSetting, setEnvSetting] = useState('');
  const [envCoveredStage, setEnvCoveredStage] = useState('');
  const [envErrors, setEnvErrors] = useState({});

  // Parking & Arrival (practical info)
  const [parkingType, setParkingType] = useState('');
  const [parkingNote, setParkingNote] = useState('');
  const [loadingZone, setLoadingZone] = useState(false);
  const [loadingZoneNote, setLoadingZoneNote] = useState('');
  const [parkingErrors, setParkingErrors] = useState({});

  // Gig Timeline (Tier 2: Downtime transparency) — all optional
  const [tlCallTime, setTlCallTime] = useState('');
  const [tlCheckType, setTlCheckType] = useState('none');
  const [tlSoundcheckStart, setTlSoundcheckStart] = useState('');
  const [tlSoundcheckEnd, setTlSoundcheckEnd] = useState('');
  const [tlLineCheckStart, setTlLineCheckStart] = useState('');
  const [tlLineCheckEnd, setTlLineCheckEnd] = useState('');
  const [tlDoorsOpen, setTlDoorsOpen] = useState('');
  const [tlSetStart, setTlSetStart] = useState('');
  const [tlSetEnd, setTlSetEnd] = useState('');
  const [tlCurfew, setTlCurfew] = useState('');
  const [tlLoadOutBy, setTlLoadOutBy] = useState('');
  const [tlDowntimePaid, setTlDowntimePaid] = useState(false);
  const [tlCanLeave, setTlCanLeave] = useState(false);
  const [tlNotes, setTlNotes] = useState('');
  const [tlErrors, setTlErrors] = useState({});

  // On-Site Contacts (Tier 1: operational — who talent calls on gig day)
  const [contacts, setContacts] = useState([]);
  const [contactErrors, setContactErrors] = useState({});

  const addContact = () => setContacts((prev) => [...prev, { name: '', role: '', phone: '', availableHours: '', isEmergency: false }]);
  const removeContact = (idx) => setContacts((prev) => prev.filter((_, i) => i !== idx));
  const updateContact = (idx, field, value) => setContacts((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));

  useEffect(() => {
    loadVenueProfile();
  }, []);

  const loadVenueProfile = async () => {
    try {
      const response = await getMyVenueProfile();
      const profile = response.data;
      setVenueProfile(profile);
      setCity(profile.city || '');
      
      // Pre-fill equipment from venue profile
      if (profile.venueProvides?.length) setVenueProvides(profile.venueProvides.join(', '));
      if (profile.talentBrings?.length) setTalentBrings(profile.talentBrings);
    } catch (error) {
      toast.error('Failed to load venue profile');
    } finally {
      setLoading(false);
    }
  };

  const addTag = (value, list, setList, setInput) => {
    if (!value.trim()) return;
    if (!list.includes(value.trim())) {
      setList([...list, value.trim()]);
    }
    setInput('');
  };

  // Address autocomplete
  const addressTimerRef = React.useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchAddressSuggestions = (query) => {
    clearTimeout(addressTimerRef.current);
    if (query.length < 3) { setAddressSuggestions([]); setShowSuggestions(false); return; }
    setAddressLoading(true);
    addressTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/geocode-suggest?q=${encodeURIComponent(query)}`);
        if (res.ok) {
          const data = await res.json();
          setAddressSuggestions(data.suggestions || []);
          setShowSuggestions(true);
        }
      } catch {} finally { setAddressLoading(false); }
    }, 300);
  };

  const selectAddress = (suggestion) => {
    setVenueAddress(suggestion.label);
    if (suggestion.city) setCity(suggestion.city);
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  // Close suggestions on outside click
  React.useEffect(() => {
    const handler = (e) => { if (addressRef.current && !addressRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const removeTag = (value, list, setList) => {
    setList(list.filter((v) => v !== value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!venueProfile?.isComplete) {
      toast.error('Please complete your venue profile before creating requests');
      navigate('/venues/me');
      return;
    }

    if (!title || !city || !eventDate || !startTime || !endTime || !budgetAmount) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Role & Execution validation
    const rxErrors = {};
    if (!execSetsCount || execSetsCount < 1 || execSetsCount > 4) rxErrors.setsCount = 'Pick 1–4';
    const setLenNum = parseInt(execSetLength);
    if (!execSetLength || isNaN(setLenNum) || setLenNum < 15 || setLenNum > 180) rxErrors.setLength = 'Enter 15–180 minutes';
    if (execSetsCount > 1) {
      const brkNum = parseInt(execBreakLength);
      if (execBreakLength === '' || isNaN(brkNum) || brkNum < 0 || brkNum > 60) rxErrors.breakLength = 'Enter 0–60 minutes';
    }
    if (!execPerformanceMode) rxErrors.performanceMode = 'Required';
    if (!execRoleType) rxErrors.roleType = 'Required';
    if (!execBillPosition) rxErrors.billPosition = 'Required';
    if (!execMaterialSource) rxErrors.materialSource = 'Required';
    if (execNotes && execNotes.length > 280) rxErrors.notes = 'Max 280 characters';
    if (Object.keys(rxErrors).length) {
      setExecErrors(rxErrors);
      toast.error('Please complete the Role & Execution section');
      return;
    }
    setExecErrors({});

    // Hospitality validation
    const hErrors = {};
    if (!hospFood) hErrors.food = 'Required';
    if (!hospDressingRoom) hErrors.dressingRoom = 'Required';
    if (!hospSecureStorage) hErrors.secureStorage = 'Required';
    if (!hospWifi) hErrors.wifi = 'Required';
    if (!hospDrinks) hErrors.drinks = 'Required';
    if (!hospShower) hErrors.showerAccess = 'Required';
    if (!hospGreenRoom) hErrors.greenRoom = 'Required';
    if (hospFoodDetail && hospFoodDetail.length > 100) hErrors.foodDetail = 'Max 100 characters';
    if (Object.keys(hErrors).length) {
      setHospErrors(hErrors);
      toast.error('Please complete the Hospitality section');
      return;
    }
    setHospErrors({});

    // Event Environment validation
    const eErrors = {};
    if (!envSetting) eErrors.setting = 'Required';
    const needsCovered = envSetting === 'outdoor' || envSetting === 'mixed';
    if (needsCovered && envCoveredStage === '') eErrors.coveredStage = 'Required for outdoor/mixed';
    if (Object.keys(eErrors).length) {
      setEnvErrors(eErrors);
      toast.error('Please complete the Event Environment section');
      return;
    }
    setEnvErrors({});

    // Parking & Arrival validation
    const pErrors = {};
    if (!parkingType) pErrors.parkingType = 'Required';
    if (parkingNote && parkingNote.length > 500) pErrors.parkingNote = 'Max 500 characters';
    if (loadingZone && loadingZoneNote && loadingZoneNote.length > 300) pErrors.loadingZoneNote = 'Max 300 characters';
    if (Object.keys(pErrors).length) {
      setParkingErrors(pErrors);
      toast.error('Please complete the Parking & Arrival section');
      return;
    }
    setParkingErrors({});

    // Gig Timeline validation — only when the venue actually entered timeline data.
    // Mirrors the backend GigTimeline check-time validator so we never eat a 422.
    const timelineFilled = [
      tlCallTime, tlSoundcheckStart, tlSoundcheckEnd, tlLineCheckStart, tlLineCheckEnd,
      tlDoorsOpen, tlSetStart, tlSetEnd, tlCurfew, tlLoadOutBy,
    ].some(Boolean) || tlNotes.trim() || tlDowntimePaid || tlCanLeave;
    const tErrors = {};
    if (timelineFilled) {
      if (tlCheckType === 'soundcheck' && !(tlSoundcheckStart && tlSoundcheckEnd)) {
        tErrors.soundcheck = 'Enter soundcheck start and end (or set check to None)';
      }
      if (tlCheckType === 'line_check' && !(tlLineCheckStart && tlLineCheckEnd)) {
        tErrors.lineCheck = 'Enter line-check start and end (or set check to None)';
      }
      if (tlCheckType === 'both' && !(tlSoundcheckStart && tlSoundcheckEnd && tlLineCheckStart && tlLineCheckEnd)) {
        tErrors.both = 'Enter all four check times (or change the check type)';
      }
    }
    if (Object.keys(tErrors).length) {
      setTlErrors(tErrors);
      toast.error('Please complete the Gig Timeline check times');
      return;
    }
    setTlErrors({});

    // Build timeline only when filled — empty stays null so existing POSTs/backend tests don't break.
    const scActive = tlCheckType === 'soundcheck' || tlCheckType === 'both';
    const lcActive = tlCheckType === 'line_check' || tlCheckType === 'both';
    const timeline = timelineFilled ? {
      callTime: tlCallTime || null,
      checkType: tlCheckType,
      soundcheckStart: scActive ? (tlSoundcheckStart || null) : null,
      soundcheckEnd: scActive ? (tlSoundcheckEnd || null) : null,
      lineCheckStart: lcActive ? (tlLineCheckStart || null) : null,
      lineCheckEnd: lcActive ? (tlLineCheckEnd || null) : null,
      doorsOpen: tlDoorsOpen || null,
      setStart: tlSetStart || null,
      setEnd: tlSetEnd || null,
      curfew: tlCurfew || null,
      loadOutBy: tlLoadOutBy || null,
      downtimePaid: tlDowntimePaid,
      canLeaveDuringDowntime: tlCanLeave,
      notes: tlNotes.trim() ? tlNotes.trim() : null,
    } : null;

    // On-Site Contacts validation — each added row needs name + role (model requires both)
    const cErrors = {};
    contacts.forEach((c, i) => {
      if (!c.name.trim()) cErrors[`name-${i}`] = 'Required';
      if (!c.role.trim()) cErrors[`role-${i}`] = 'Required';
    });
    if (Object.keys(cErrors).length) {
      setContactErrors(cErrors);
      toast.error('Each contact needs a name and role');
      return;
    }
    setContactErrors({});

    setSubmitting(true);
    try {
      await createRequest({
        title,
        description: description || null,
        city,
        venueAddress: venueAddress || null,
        eventDate,
        startTime,
        endTime,
        roleTypeNeeded,
        roleSubtype: roleSubtype || null,
        budgetAmount: parseFloat(budgetAmount),
        currency: 'EUR',
        rateType: rateType || null,
        urgency,
        expectedAttendance: expectedAttendance ? parseInt(expectedAttendance) : null,
        parkingType,
        parkingNote: parkingNote.trim() ? parkingNote.trim() : null,
        loadingZone,
        loadingZoneNote: loadingZone && loadingZoneNote.trim() ? loadingZoneNote.trim() : null,
        bookingTerms: {
          arrivalTime: arrivalTime || null,
          startTime: startTime || null,
          endTime: endTime || null,
          hardStop,
          payRateType,
          bookingCovers: bookingCovers || null,
          beyondThat,
          soundcheckIncluded,
          soundcheckDuration: soundcheckDuration ? `${soundcheckDuration}min` : null,
          setupPrepTime,
          downtimeStatus,
          mobility,
          primaryRole: primaryRole || roleSubtype || null,
          roleIncludes,
          roleExcludes,
          venueProvides: venueProvides || null,
          talentBrings,
          optionalItems,
          equipmentFailurePolicy,
          includedRadiusKm: includedRadiusKm ? parseInt(includedRadiusKm) : null,
          beyondRadiusRate: beyondRadiusRate ? parseFloat(beyondRadiusRate) : null,
          parkingCovered,
          tollsCovered,
        },
        roleExecution: {
          setsCount: execSetsCount,
          setLengthMinutes: parseInt(execSetLength),
          breakLengthMinutes: execSetsCount > 1 ? parseInt(execBreakLength) : null,
          performanceMode: execPerformanceMode,
          roleType: execRoleType,
          billPosition: execBillPosition,
          materialSource: execMaterialSource,
          announcementLanguage: execAnnouncementLanguage || null,
          notes: execNotes.trim() ? execNotes.trim() : null,
        },
        hospitality: {
          food: hospFood,
          foodDetail: hospFoodDetail.trim() ? hospFoodDetail.trim() : null,
          dressingRoom: hospDressingRoom,
          secureStorage: hospSecureStorage,
          wifi: hospWifi,
          drinks: hospDrinks,
          showerAccess: hospShower,
          greenRoom: hospGreenRoom,
        },
        catering: cateringProvided
          ? {
              provided: cateringProvided,
              served_at: cateringProvided !== 'none' && cateringServedAt ? cateringServedAt : null,
              vegetarian: cateringProvided !== 'none' && cateringVegetarian
                ? cateringVegetarian
                : null,
            }
          : null,
        environment: {
          setting: envSetting,
          coveredStage: (envSetting === 'outdoor' || envSetting === 'mixed')
            ? (envCoveredStage === 'true')
            : null,
        },
        timeline,
        contacts: contacts.map((c) => ({
          name: c.name.trim(),
          role: c.role.trim(),
          phone: c.phone.trim() ? c.phone.trim() : null,
          availableHours: c.availableHours.trim() ? c.availableHours.trim() : null,
          isEmergency: c.isEmergency,
        })),
      });
      toast.success('Request published!');
      navigate('/venues/requests');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="content-container">
        <div className="h-64 bg-card animate-pulse rounded-lg"></div>
      </div>
    );
  }

  if (!venueProfile?.isComplete) {
    return (
      <div className="content-container">
        <div className="surface-card p-8 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-[var(--amber)] mb-4" />
          <h2 className="hero-title mb-2">Complete Your Profile First</h2>
          <p className="body-text mb-4">
            You need to complete your venue profile before creating requests.
          </p>
          <Button onClick={() => navigate('/venues/me')} className="btn-primary" data-testid="go-to-profile-btn">
            Complete Profile
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="content-container pb-32 md:pb-8">
      <div className="mb-6">
        <h1 className="page-title gold-accent">New Request</h1>
        <p className="body-text mt-2">Find talent for your event</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="surface-card">
          <div className="p-4 border-b border-[var(--b1)]">
            <h3 className="section-header text-[var(--t0)]">Request Details</h3>
          </div>
          <div className="p-5 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="micro-label">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Jazz Vocalist Needed for Saturday Night"
                className="input-stage8 h-12"
                data-testid="request-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="micro-label">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Provide details about the gig, requirements, etc."
                rows={4}
                className="input-stage8"
                data-testid="request-description-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="micro-label">Looking for *</Label>
                <Select value={roleTypeNeeded} onValueChange={setRoleTypeNeeded}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="role-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="MUSICIAN">Musician</SelectItem>
                    <SelectItem value="TECH">Technical Crew</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="roleSubtype" className="micro-label">Specific Role</Label>
                <Input
                  id="roleSubtype"
                  value={roleSubtype}
                  onChange={(e) => setRoleSubtype(e.target.value)}
                  placeholder="e.g., Drummer, FOH Engineer"
                  className="input-stage8 h-12"
                  data-testid="role-subtype-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="micro-label">Urgency *</Label>
              <Select value={urgency} onValueChange={setUrgency}>
                <SelectTrigger className="h-12 input-stage8" data-testid="urgency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                  <SelectItem value="CRITICAL">
                    <div className="flex items-center gap-2">
                      <div className="led led-stop"></div>
                      Critical (expires in 12h)
                    </div>
                  </SelectItem>
                  <SelectItem value="TODAY">
                    <div className="flex items-center gap-2">
                      <div className="led led-pending"></div>
                      Today (expires end of day)
                    </div>
                  </SelectItem>
                  <SelectItem value="THIS_WEEK">
                    <div className="flex items-center gap-2">
                      <div className="led led-transit"></div>
                      This Week (expires in 7 days)
                    </div>
                  </SelectItem>
                  <SelectItem value="FLEXIBLE">
                    <div className="flex items-center gap-2">
                      <div className="led led-off"></div>
                      Flexible (expires in 14 days)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Event Details */}
        <div className="surface-card">
          <div className="p-4 border-b border-[var(--b1)]">
            <h3 className="section-header text-[var(--t0)]">Event Details</h3>
          </div>
          <div className="p-5 space-y-4">
            <div className="space-y-2" ref={addressRef}>
              <Label htmlFor="venueAddress" className="micro-label">Venue Address</Label>
              <div className="relative">
                <Input
                  id="venueAddress"
                  value={venueAddress}
                  onChange={(e) => { setVenueAddress(e.target.value); fetchAddressSuggestions(e.target.value); }}
                  onFocus={() => { if (addressSuggestions.length > 0) setShowSuggestions(true); }}
                  placeholder="Start typing an address..."
                  className="input-stage8 h-12"
                  autoComplete="off"
                  data-testid="request-address-input"
                />
                {addressLoading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="h-4 w-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--t3)', borderTopColor: 'var(--amber)' }} />
                  </div>
                )}
                {showSuggestions && addressSuggestions.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 mt-1 rounded-lg overflow-hidden shadow-lg" style={{ background: 'var(--bg-surface)', border: '1px solid var(--b1-solid)', maxHeight: '200px', overflowY: 'auto' }} data-testid="address-suggestions">
                    {addressSuggestions.map((s, i) => (
                      <button
                        key={s.label || i}
                        type="button"
                        onClick={() => selectAddress(s)}
                        className="w-full text-left px-3 py-2.5 text-sm transition-colors hover:bg-[var(--bg-card-inner)]"
                        style={{ color: 'var(--t1)', borderBottom: '1px solid var(--b1-solid)' }}
                        data-testid={`address-suggestion-${i}`}
                      >
                        <div className="truncate" style={{ fontFamily: 'var(--font-body)' }}>{s.label}</div>
                        {s.city && <div className="text-xs mt-0.5" style={{ color: 'var(--t3)' }}>{s.city}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="city" className="micro-label">City *</Label>
              <Input
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Amsterdam"
                className="input-stage8 h-12"
                data-testid="request-city-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="eventDate" className="micro-label">Date *</Label>
                <Input
                  id="eventDate"
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="input-stage8 h-12"
                  data-testid="event-date-input"
                />
              </div>
            </div>

            {/* Time Fields - Custom 24h Input */}
            <div className="mt-4">
              <Label className="micro-label mb-3 block">Event Times *</Label>
              <TimeFieldGroup>
                <TimeField
                  label="Start"
                  value={startTime}
                  onChange={setStartTime}
                  testId="start-time-input"
                />
                <TimeField
                  label="End"
                  value={endTime}
                  onChange={setEndTime}
                  pairedValue={startTime}
                  pairedLabel="Start"
                  showDuration={true}
                  testId="end-time-input"
                />
              </TimeFieldGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expectedAttendance" className="micro-label flex items-center gap-2">
                <Users className="h-4 w-4 text-[var(--t2)]" />
                Expected Attendance
              </Label>
              <Input
                id="expectedAttendance"
                type="number"
                value={expectedAttendance}
                onChange={(e) => setExpectedAttendance(e.target.value)}
                placeholder="e.g. 200"
                className="input-stage8 h-12 w-full md:w-1/3"
                data-testid="expected-attendance-input"
                min="1"
              />
              <p className="data-text">Estimated number of attendees for this event</p>
            </div>
          </div>
        </div>

        {/* Budget */}
        <div className="surface-card">
          <div className="p-4 border-b border-[var(--b1)]">
            <h3 className="section-header text-[var(--t0)]">Budget</h3>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="budgetAmount" className="micro-label">Budget Amount (EUR) *</Label>
                <Input
                  id="budgetAmount"
                  type="number"
                  value={budgetAmount}
                  onChange={(e) => setBudgetAmount(e.target.value)}
                  placeholder="200"
                  className="input-stage8 h-12"
                  data-testid="budget-amount-input"
                  required
                  min="1"
                />
              </div>
              <div className="space-y-2">
                <Label className="micro-label">Rate Type</Label>
                <Select value={rateType} onValueChange={setRateType}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="request-rate-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="HOURLY">Per Hour</SelectItem>
                    <SelectItem value="FLAT_EVENT">Flat Rate</SelectItem>
                    <SelectItem value="PART_OF_DAY">Part of Day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Role & Execution Section */}
        <div className="surface-card" data-testid="role-execution-section">
          <div className="p-4 border-b border-[var(--b1)]">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[var(--t2)]" />
              <h3 className="section-header text-[var(--t0)]">Role & Execution</h3>
            </div>
            <p className="data-text mt-1">How the performance is structured.</p>
          </div>
          <div className="p-5 space-y-5">
            {/* Sets count — chips */}
            <div className="space-y-2">
              <Label className="micro-label">How many sets? *</Label>
              <div className="flex gap-2" data-testid="exec-sets-chips">
                {[1, 2, 3, 4].map((n) => {
                  const active = execSetsCount === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => {
                        setExecSetsCount(n);
                        if (n === 1) setExecBreakLength('');
                        else if (!execBreakLength) setExecBreakLength('15');
                      }}
                      className="h-11 w-14 rounded-lg text-sm font-semibold transition-colors"
                      style={{
                        border: `1px solid ${active ? 'var(--s8-amber)' : 'var(--s8-b1-solid)'}`,
                        background: active ? 'rgba(200,146,62,0.12)' : 'var(--bg-card-inner)',
                        color: active ? 'var(--s8-amber)' : 'var(--t1)',
                        fontFamily: 'var(--s8-font-mono)',
                      }}
                      data-testid={`exec-sets-${n}`}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
              {execErrors.setsCount && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{execErrors.setsCount}</p>}
            </div>

            {/* Set length & Break */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="exec-set-length" className="micro-label">Set Length (minutes) *</Label>
                <Input
                  id="exec-set-length"
                  type="number"
                  value={execSetLength}
                  onChange={(e) => setExecSetLength(e.target.value)}
                  placeholder="45"
                  className="input-stage8 h-12"
                  min="15"
                  max="180"
                  data-testid="exec-set-length-input"
                />
                {execErrors.setLength && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{execErrors.setLength}</p>}
              </div>
              {execSetsCount > 1 && (
                <div className="space-y-2">
                  <Label htmlFor="exec-break-length" className="micro-label">Break Between Sets (minutes) *</Label>
                  <Input
                    id="exec-break-length"
                    type="number"
                    value={execBreakLength}
                    onChange={(e) => setExecBreakLength(e.target.value)}
                    placeholder="15"
                    className="input-stage8 h-12"
                    min="0"
                    max="60"
                    data-testid="exec-break-length-input"
                  />
                  {execErrors.breakLength && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{execErrors.breakLength}</p>}
                </div>
              )}
            </div>

            {/* Mode & Role */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="micro-label">Performance Mode *</Label>
                <Select value={execPerformanceMode} onValueChange={setExecPerformanceMode}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="exec-performance-mode-select">
                    <SelectValue placeholder="Select mode" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="solo">Solo</SelectItem>
                    <SelectItem value="duo">Duo</SelectItem>
                    <SelectItem value="trio">Trio</SelectItem>
                    <SelectItem value="quartet">Quartet</SelectItem>
                    <SelectItem value="band">Band</SelectItem>
                    <SelectItem value="backing_band_provided">Backing band provided</SelectItem>
                  </SelectContent>
                </Select>
                {execErrors.performanceMode && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{execErrors.performanceMode}</p>}
              </div>
              <div className="space-y-2">
                <Label className="micro-label">Role Type *</Label>
                <Select value={execRoleType} onValueChange={setExecRoleType}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="exec-role-type-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="support">Support</SelectItem>
                    <SelectItem value="accompaniment">Accompaniment</SelectItem>
                    <SelectItem value="house_band">House band</SelectItem>
                  </SelectContent>
                </Select>
                {execErrors.roleType && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{execErrors.roleType}</p>}
              </div>
            </div>

            {/* Bill position — full-width */}
            <div className="space-y-2">
              <Label className="micro-label">Bill Position *</Label>
              <Select value={execBillPosition} onValueChange={setExecBillPosition}>
                <SelectTrigger className="h-12 input-stage8" data-testid="exec-bill-position-select">
                  <SelectValue placeholder="Select bill position" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                  <SelectItem value="solo_bill">Solo bill</SelectItem>
                  <SelectItem value="headliner">Headliner</SelectItem>
                  <SelectItem value="direct_support">Direct support</SelectItem>
                  <SelectItem value="opener">Opener</SelectItem>
                  <SelectItem value="multi_act">Multi-act bill</SelectItem>
                </SelectContent>
              </Select>
              {execErrors.billPosition && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{execErrors.billPosition}</p>}
            </div>

            {/* Material & Announcements */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="micro-label">Material Source *</Label>
                <Select value={execMaterialSource} onValueChange={setExecMaterialSource}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="exec-material-source-select">
                    <SelectValue placeholder="Select material" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="own_setlist">Own setlist</SelectItem>
                    <SelectItem value="provided_setlist">Provided setlist</SelectItem>
                    <SelectItem value="mixed">Mixed</SelectItem>
                    <SelectItem value="improvised">Improvised</SelectItem>
                  </SelectContent>
                </Select>
                {execErrors.materialSource && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{execErrors.materialSource}</p>}
              </div>
              <div className="space-y-2">
                <Label className="micro-label">Announcement Language</Label>
                <Select value={execAnnouncementLanguage || 'unset'} onValueChange={(v) => setExecAnnouncementLanguage(v === 'unset' ? '' : v)}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="exec-announcement-language-select">
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="unset">—</SelectItem>
                    <SelectItem value="dutch">Dutch</SelectItem>
                    <SelectItem value="english">English</SelectItem>
                    <SelectItem value="both">Dutch + English</SelectItem>
                    <SelectItem value="none">None</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="exec-notes" className="micro-label">Notes <span className="text-[var(--t3)] font-normal">(optional, 280 char max)</span></Label>
              <Textarea
                id="exec-notes"
                value={execNotes}
                onChange={(e) => setExecNotes(e.target.value.slice(0, 280))}
                placeholder='e.g. "Must lead Happy Birthday sing-along at 20:00"'
                className="input-stage8 min-h-[80px]"
                data-testid="exec-notes-input"
              />
              <div className="flex justify-between text-xs" style={{ color: 'var(--t3)' }}>
                {execErrors.notes ? <span style={{ color: 'var(--s8-red-flag)' }}>{execErrors.notes}</span> : <span />}
                <span>{execNotes.length}/280</span>
              </div>
            </div>
          </div>
        </div>

        {/* Hospitality Section */}
        <div className="surface-card" data-testid="hospitality-section">
          <div className="p-4 border-b border-[var(--b1)]">
            <div className="flex items-center gap-2">
              <Coffee className="h-5 w-5 text-[var(--t2)]" />
              <h3 className="section-header text-[var(--t0)]">Hospitality</h3>
            </div>
            <p className="data-text mt-1">What you provide backstage and on-site.</p>
          </div>
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="micro-label">Food *</Label>
                <Select value={hospFood} onValueChange={setHospFood}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="hosp-food-select"><SelectValue placeholder="Select food option" /></SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="not_provided">Not provided</SelectItem>
                    <SelectItem value="snacks">Snacks</SelectItem>
                    <SelectItem value="meal_provided">Meal provided</SelectItem>
                    <SelectItem value="meal_voucher">Meal voucher</SelectItem>
                    <SelectItem value="rider_specific">Per rider</SelectItem>
                  </SelectContent>
                </Select>
                {hospErrors.food && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.food}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="hosp-food-detail" className="micro-label">Food Detail <span className="text-[var(--t3)] font-normal">(optional, 100 char max)</span></Label>
                <Input
                  id="hosp-food-detail"
                  value={hospFoodDetail}
                  onChange={(e) => setHospFoodDetail(e.target.value.slice(0, 100))}
                  placeholder='e.g. "Vegetarian buffet at 19:30"'
                  className="input-stage8 h-12"
                  data-testid="hosp-food-detail-input"
                />
                {hospErrors.foodDetail && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.foodDetail}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="micro-label">Catering <span className="text-[var(--t3)] font-normal">(optional)</span></Label>
                <Select
                  value={cateringProvided}
                  onValueChange={(v) => {
                    setCateringProvided(v);
                    if (v === 'none') { setCateringServedAt(''); setCateringVegetarian(''); }
                  }}
                >
                  <SelectTrigger className="h-12 input-stage8" data-testid="catering-provided-select"><SelectValue placeholder="Not specified" /></SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="at_venue">At venue</SelectItem>
                    <SelectItem value="on_request">On request</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="catering-served-at" className="micro-label">Served At</Label>
                <Input
                  id="catering-served-at"
                  type="time"
                  value={cateringServedAt}
                  onChange={(e) => setCateringServedAt(e.target.value)}
                  disabled={!cateringProvided || cateringProvided === 'none'}
                  className="input-stage8 h-12"
                  data-testid="catering-served-at-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="micro-label">Vegetarian</Label>
                <Select
                  value={cateringVegetarian}
                  onValueChange={setCateringVegetarian}
                  disabled={!cateringProvided || cateringProvided === 'none'}
                >
                  <SelectTrigger className="h-12 input-stage8" data-testid="catering-vegetarian-select"><SelectValue placeholder="Not specified" /></SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="unavailable">Unavailable</SelectItem>
                    <SelectItem value="ask">Ask ahead</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="micro-label">Dressing Room *</Label>
                <Select value={hospDressingRoom} onValueChange={setHospDressingRoom}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="hosp-dressing-room-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="shared">Shared</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                  </SelectContent>
                </Select>
                {hospErrors.dressingRoom && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.dressingRoom}</p>}
              </div>
              <div className="space-y-2">
                <Label className="micro-label">Secure Storage *</Label>
                <Select value={hospSecureStorage} onValueChange={setHospSecureStorage}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="hosp-secure-storage-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="lockable_room">Lockable room</SelectItem>
                    <SelectItem value="safe">Safe</SelectItem>
                    <SelectItem value="venue_supervised">Venue supervised</SelectItem>
                  </SelectContent>
                </Select>
                {hospErrors.secureStorage && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.secureStorage}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="micro-label">WiFi *</Label>
                <Select value={hospWifi} onValueChange={setHospWifi}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="hosp-wifi-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="guest_network">Guest network</SelectItem>
                    <SelectItem value="staff_network">Staff network</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
                {hospErrors.wifi && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.wifi}</p>}
              </div>
              <div className="space-y-2">
                <Label className="micro-label">Drinks *</Label>
                <Select value={hospDrinks} onValueChange={setHospDrinks}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="hosp-drinks-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="water_only">Water only</SelectItem>
                    <SelectItem value="soft_drinks">Soft drinks</SelectItem>
                    <SelectItem value="rider_specific">Per rider</SelectItem>
                    <SelectItem value="open_bar">Open bar</SelectItem>
                  </SelectContent>
                </Select>
                {hospErrors.drinks && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.drinks}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="micro-label">Shower Access *</Label>
                <Select value={hospShower} onValueChange={setHospShower}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="hosp-shower-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
                {hospErrors.showerAccess && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.showerAccess}</p>}
              </div>
              <div className="space-y-2">
                <Label className="micro-label">Green Room *</Label>
                <Select value={hospGreenRoom} onValueChange={setHospGreenRoom}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="hosp-green-room-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="shared">Shared</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                  </SelectContent>
                </Select>
                {hospErrors.greenRoom && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{hospErrors.greenRoom}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Event Environment Section */}
        <div className="surface-card" data-testid="environment-section">
          <div className="p-4 border-b border-[var(--b1)]">
            <div className="flex items-center gap-2">
              <Cloud className="h-5 w-5 text-[var(--t2)]" />
              <h3 className="section-header text-[var(--t0)]">Event Environment</h3>
            </div>
            <p className="data-text mt-1">Where and how the gig takes place.</p>
          </div>
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="micro-label">Setting *</Label>
                <Select value={envSetting} onValueChange={(v) => { setEnvSetting(v); if (v === 'indoor' || v === 'covered_outdoor') setEnvCoveredStage(''); }}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="env-setting-select"><SelectValue placeholder="Select setting" /></SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="indoor">Indoor</SelectItem>
                    <SelectItem value="outdoor">Outdoor</SelectItem>
                    <SelectItem value="covered_outdoor">Covered outdoor</SelectItem>
                    <SelectItem value="mixed">Mixed (indoor + outdoor)</SelectItem>
                  </SelectContent>
                </Select>
                {envErrors.setting && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{envErrors.setting}</p>}
              </div>
              {(envSetting === 'outdoor' || envSetting === 'mixed') && (
                <div className="space-y-2">
                  <Label className="micro-label">Covered Stage *</Label>
                  <Select value={envCoveredStage} onValueChange={setEnvCoveredStage}>
                    <SelectTrigger className="h-12 input-stage8" data-testid="env-covered-stage-select"><SelectValue placeholder="Select option" /></SelectTrigger>
                    <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                      <SelectItem value="true">Yes</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                  {envErrors.coveredStage && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{envErrors.coveredStage}</p>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Gig Timeline Section */}
        <div className="surface-card" data-testid="timeline-section">
          <div className="p-4 border-b border-[var(--b1)]">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-[var(--t2)]" />
              <h3 className="section-header text-[var(--t0)]">Gig Timeline</h3>
            </div>
            <p className="data-text mt-1">Optional. The full day — load-in to load-out. Helps talent see downtime up front.</p>
          </div>
          <div className="p-5 space-y-5">
            <div>
              <Label className="micro-label mb-3 block">Call Time & Doors</Label>
              <TimeFieldGroup>
                <TimeField label="Call time" value={tlCallTime} onChange={setTlCallTime} testId="tl-call-time-input" />
                <TimeField label="Doors open" value={tlDoorsOpen} onChange={setTlDoorsOpen} testId="tl-doors-open-input" />
              </TimeFieldGroup>
              {/* Each request is one role, so call time is that person's arrival. The detail page
                  counts their time on site from it. */}
              <p className="data-text mt-2" data-testid="tl-call-time-help">Call time is when the person you book needs to arrive, not when the first act loads in.</p>
            </div>

            <div className="space-y-2">
              <Label className="micro-label">Pre-Show Check</Label>
              <Select value={tlCheckType} onValueChange={setTlCheckType}>
                <SelectTrigger className="h-12 input-stage8 md:w-1/2" data-testid="tl-check-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="soundcheck">Soundcheck</SelectItem>
                  <SelectItem value="line_check">Line check</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(tlCheckType === 'soundcheck' || tlCheckType === 'both') && (
              <div>
                <Label className="micro-label mb-3 block">Soundcheck</Label>
                <TimeFieldGroup>
                  <TimeField label="Start" value={tlSoundcheckStart} onChange={setTlSoundcheckStart} testId="tl-soundcheck-start-input" />
                  <TimeField label="End" value={tlSoundcheckEnd} onChange={setTlSoundcheckEnd} testId="tl-soundcheck-end-input" />
                </TimeFieldGroup>
                {tlErrors.soundcheck && <p className="text-xs mt-1" style={{ color: 'var(--s8-red-flag)' }}>{tlErrors.soundcheck}</p>}
              </div>
            )}

            {(tlCheckType === 'line_check' || tlCheckType === 'both') && (
              <div>
                <Label className="micro-label mb-3 block">Line Check</Label>
                <TimeFieldGroup>
                  <TimeField label="Start" value={tlLineCheckStart} onChange={setTlLineCheckStart} testId="tl-linecheck-start-input" />
                  <TimeField label="End" value={tlLineCheckEnd} onChange={setTlLineCheckEnd} testId="tl-linecheck-end-input" />
                </TimeFieldGroup>
                {tlErrors.lineCheck && <p className="text-xs mt-1" style={{ color: 'var(--s8-red-flag)' }}>{tlErrors.lineCheck}</p>}
              </div>
            )}
            {tlErrors.both && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{tlErrors.both}</p>}

            <div>
              <Label className="micro-label mb-3 block">Show</Label>
              <TimeFieldGroup>
                <TimeField label="Set start" value={tlSetStart} onChange={setTlSetStart} testId="tl-set-start-input" />
                <TimeField label="Set end" value={tlSetEnd} onChange={setTlSetEnd} pairedValue={tlSetStart} pairedLabel="Set start" showDuration testId="tl-set-end-input" />
              </TimeFieldGroup>
            </div>

            <div>
              <Label className="micro-label mb-3 block">Curfew & Load-Out</Label>
              <TimeFieldGroup>
                <TimeField label="Curfew" value={tlCurfew} onChange={setTlCurfew} testId="tl-curfew-input" />
                <TimeField label="Load-out by" value={tlLoadOutBy} onChange={setTlLoadOutBy} testId="tl-loadout-input" />
              </TimeFieldGroup>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="micro-label">Downtime Paid</Label>
                <div className="flex items-center justify-between h-12 px-3 input-stage8">
                  <span className="text-sm">{tlDowntimePaid ? 'Paid' : 'Unpaid'}</span>
                  <Switch checked={tlDowntimePaid} onCheckedChange={setTlDowntimePaid} data-testid="tl-downtime-paid-switch" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="micro-label">Can Leave During Downtime</Label>
                <div className="flex items-center justify-between h-12 px-3 input-stage8">
                  <span className="text-sm">{tlCanLeave ? 'Yes' : 'No'}</span>
                  <Switch checked={tlCanLeave} onCheckedChange={setTlCanLeave} data-testid="tl-can-leave-switch" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tl-notes" className="micro-label">Timeline Notes <span className="text-[var(--t3)] font-normal">(optional, 500 char max)</span></Label>
              <Textarea
                id="tl-notes"
                value={tlNotes}
                onChange={(e) => setTlNotes(e.target.value.slice(0, 500))}
                placeholder="e.g. Support act 20:00–20:40. Your set 21:00. Free during opener."
                className="input-stage8"
                data-testid="tl-notes-input"
              />
            </div>
          </div>
        </div>

        {/* On-Site Contacts Section */}
        <div className="surface-card" data-testid="contacts-section">
          <div className="p-4 border-b border-[var(--b1)]">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-[var(--t2)]" />
                <h3 className="section-header text-[var(--t0)]">On-Site Contacts</h3>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addContact} data-testid="add-contact-btn" className="gap-1">
                <Plus className="h-4 w-4" /> Add contact
              </Button>
            </div>
            <p className="data-text mt-1">Who talent calls on gig day. Optional — add as many as you need.</p>
          </div>
          <div className="p-5 space-y-4">
            {contacts.length === 0 && (
              <p className="text-sm text-muted-foreground">No contacts added yet.</p>
            )}
            {contacts.map((c, i) => (
              <div key={i} className="rounded-md border border-border p-4 space-y-3" data-testid={`contact-row-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="micro-label">Name *</Label>
                    <Input value={c.name} onChange={(e) => updateContact(i, 'name', e.target.value)} placeholder="e.g. Sanne de Vries" className="input-stage8" data-testid={`contact-name-${i}`} />
                    {contactErrors[`name-${i}`] && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{contactErrors[`name-${i}`]}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label className="micro-label">Role *</Label>
                    <Input value={c.role} onChange={(e) => updateContact(i, 'role', e.target.value)} placeholder="e.g. Stage Manager" className="input-stage8" data-testid={`contact-role-${i}`} />
                    {contactErrors[`role-${i}`] && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{contactErrors[`role-${i}`]}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="micro-label">Phone</Label>
                    <Input value={c.phone} onChange={(e) => updateContact(i, 'phone', e.target.value)} placeholder="+31 6 12345678" className="input-stage8" data-testid={`contact-phone-${i}`} />
                  </div>
                  <div className="space-y-2">
                    <Label className="micro-label">Available Hours</Label>
                    <Input value={c.availableHours} onChange={(e) => updateContact(i, 'availableHours', e.target.value)} placeholder="09:00–22:00" className="input-stage8" data-testid={`contact-hours-${i}`} />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch checked={c.isEmergency} onCheckedChange={(v) => updateContact(i, 'isEmergency', v)} data-testid={`contact-emergency-${i}`} />
                    <span className="text-sm">Emergency contact</span>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeContact(i)} data-testid={`remove-contact-${i}`} className="text-muted-foreground gap-1">
                    <X className="h-4 w-4" /> Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Parking & Arrival Section */}
        <div className="surface-card" data-testid="parking-section">
          <div className="p-4 border-b border-[var(--b1)]">
            <div className="flex items-center gap-2">
              <Car className="h-5 w-5 text-[var(--t2)]" />
              <h3 className="section-header text-[var(--t0)]">Parking &amp; Arrival</h3>
            </div>
            <p className="data-text mt-1">How and where talent arrives and unloads.</p>
          </div>
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="micro-label">Parking Type *</Label>
                <Select value={parkingType} onValueChange={setParkingType}>
                  <SelectTrigger className="h-12 input-stage8" data-testid="parking-type-select"><SelectValue placeholder="Select parking" /></SelectTrigger>
                  <SelectContent className="bg-[var(--s1)] border-[var(--b2)]">
                    <SelectItem value="onsite">On-site</SelectItem>
                    <SelectItem value="street">Street</SelectItem>
                    <SelectItem value="garage">Garage</SelectItem>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {parkingErrors.parkingType && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{parkingErrors.parkingType}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="micro-label">Details</Label>
              <Textarea
                value={parkingNote}
                onChange={(e) => setParkingNote(e.target.value)}
                placeholder="Location, garage name, gate code, permit — e.g. Q-Park Museumplein, enter via Hobbemastraat. Gate code 1845 after 18:00."
                maxLength={500}
                rows={3}
                className="input-stage8"
                data-testid="parking-note-input"
              />
              {parkingErrors.parkingNote && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{parkingErrors.parkingNote}</p>}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between h-12 px-3 bg-secondary border border-border rounded-md">
                <div className="flex items-center gap-2">
                  <Truck className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Loading zone available</span>
                </div>
                <Switch checked={loadingZone} onCheckedChange={setLoadingZone} data-testid="loading-zone-switch" />
              </div>
              {loadingZone && (
                <div className="space-y-2">
                  <Textarea
                    value={loadingZoneNote}
                    onChange={(e) => setLoadingZoneNote(e.target.value)}
                    placeholder="e.g. Bay at rear, 20m to stage door"
                    maxLength={300}
                    rows={2}
                    className="input-stage8"
                    data-testid="loading-zone-note-input"
                  />
                  {parkingErrors.loadingZoneNote && <p className="text-xs" style={{ color: 'var(--s8-red-flag)' }}>{parkingErrors.loadingZoneNote}</p>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Booking Terms Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-[var(--t2)]">
            <Briefcase className="h-5 w-5" />
            <h2 className="section-header text-[var(--t0)]">Booking Terms</h2>
          </div>
          <p className="data-text -mt-2">Both sides agree to these before confirming.</p>
          <p className="text-xs text-muted-foreground -mt-1">Check each section to include it in your request. Talent sees exactly what you set here.</p>

          {/* Call Time */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                Call Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TimeFieldGroup>
                <TimeField
                  label="Arrival"
                  value={arrivalTime}
                  onChange={setArrivalTime}
                  testId="arrival-time-input"
                />
                <TimeField
                  label="Start"
                  value={startTime}
                  onChange={() => {}} // Read-only, synced from Event Details
                  testId="call-start-time"
                />
                <TimeField
                  label="End"
                  value={endTime}
                  onChange={() => {}} // Read-only, synced from Event Details
                  pairedValue={startTime}
                  showDuration={true}
                  testId="call-end-time"
                />
                <div className="space-y-2">
                  <label className="text-[11px] uppercase tracking-wider text-[var(--t2,#666)] font-medium mb-1.5 block">
                    Hard stop?
                  </label>
                  <Select value={hardStop} onValueChange={setHardStop}>
                    <SelectTrigger className="h-10 bg-secondary border-border w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Flexible">Flexible</SelectItem>
                      <SelectItem value="Fixed">Fixed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TimeFieldGroup>
            </CardContent>
          </Card>

          {/* Pay */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <Euro className="h-4 w-4 text-muted-foreground" />
                Pay
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Rate Type</Label>
                  <Select value={payRateType} onValueChange={setPayRateType}>
                    <SelectTrigger className="h-10 bg-secondary border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Hourly">Hourly</SelectItem>
                      <SelectItem value="Fixed">Fixed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Booking Covers</Label>
                  <Input
                    value={bookingCovers}
                    onChange={(e) => setBookingCovers(e.target.value)}
                    placeholder="e.g., Up to 4h"
                    className="h-10 bg-secondary border-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Beyond That</Label>
                  <Select value={beyondThat} onValueChange={setBeyondThat}>
                    <SelectTrigger className="h-10 bg-secondary border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Same rate applies">Same rate applies</SelectItem>
                      <SelectItem value="Overtime rate (1.5x)">Overtime rate (1.5x)</SelectItem>
                      <SelectItem value="To be negotiated">To be negotiated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Prep & Soundcheck */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <Settings className="h-4 w-4 text-muted-foreground" />
                Prep & Soundcheck
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Soundcheck</Label>
                  <div className="flex items-center justify-between h-10 px-3 bg-secondary border border-border rounded-md">
                    <span className="text-sm">{soundcheckIncluded ? 'Included' : 'Not included'}</span>
                    <Switch
                      checked={soundcheckIncluded}
                      onCheckedChange={setSoundcheckIncluded}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Duration</Label>
                  <Select value={soundcheckDuration} onValueChange={setSoundcheckDuration}>
                    <SelectTrigger className="h-10 bg-secondary border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15min</SelectItem>
                      <SelectItem value="30">30min</SelectItem>
                      <SelectItem value="45">45min</SelectItem>
                      <SelectItem value="60">60min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Setup/Prep Time</Label>
                  <Select value={setupPrepTime} onValueChange={setSetupPrepTime}>
                    <SelectTrigger className="h-10 bg-secondary border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Paid">Paid</SelectItem>
                      <SelectItem value="Unpaid">Unpaid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Downtime Policy */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                Downtime Policy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Status</Label>
                  <div className="flex items-center gap-2">
                    <Select value={downtimeStatus} onValueChange={setDowntimeStatus}>
                      <SelectTrigger className="h-10 bg-secondary border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Paid">Paid</SelectItem>
                        <SelectItem value="Unpaid">Unpaid</SelectItem>
                      </SelectContent>
                    </Select>
                    {downtimeStatus === 'Paid' && (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Paid</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">All on-site time compensated</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Mobility</Label>
                  <Select value={mobility} onValueChange={setMobility}>
                    <SelectTrigger className="h-10 bg-secondary border-border">
                      <SelectValue placeholder="Select policy" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Must stay on site">Must stay on site</SelectItem>
                      <SelectItem value="Can leave venue">Can leave venue</SelectItem>
                    </SelectContent>
                  </Select>
                  {mobility === 'Must stay on site' && downtimeStatus && (
                    <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/50">Must stay on site</Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Role Scope */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-muted-foreground" />
                Role Scope
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Primary</Label>
                <Input
                  value={primaryRole || roleSubtype}
                  onChange={(e) => setPrimaryRole(e.target.value)}
                  placeholder="e.g., Sound Engineer"
                  className="h-10 bg-secondary border-border"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Includes</Label>
                <div className="flex gap-2">
                  <Input
                    value={newInclude}
                    onChange={(e) => setNewInclude(e.target.value)}
                    placeholder="e.g., Setup, Soundcheck, Live mixing"
                    className="h-10 bg-secondary border-border"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag(newInclude, roleIncludes, setRoleIncludes, setNewInclude))}
                  />
                  <Button type="button" variant="outline" size="icon" className="border-border bg-secondary shrink-0" onClick={() => addTag(newInclude, roleIncludes, setRoleIncludes, setNewInclude)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {roleIncludes.map((item) => (
                    <Badge key={item} variant="secondary" className="gap-1 bg-secondary border-border">
                      {item}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(item, roleIncludes, setRoleIncludes)} />
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Excludes</Label>
                <div className="flex gap-2">
                  <Input
                    value={newExclude}
                    onChange={(e) => setNewExclude(e.target.value)}
                    placeholder="e.g., Equipment rental, Lighting"
                    className="h-10 bg-secondary border-border"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag(newExclude, roleExcludes, setRoleExcludes, setNewExclude))}
                  />
                  <Button type="button" variant="outline" size="icon" className="border-border bg-secondary shrink-0" onClick={() => addTag(newExclude, roleExcludes, setRoleExcludes, setNewExclude)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {roleExcludes.map((item) => (
                    <Badge key={item} variant="outline" className="gap-1 border-orange-500/50 text-orange-400">
                      {item}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(item, roleExcludes, setRoleExcludes)} />
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Equipment */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <Music className="h-4 w-4 text-muted-foreground" />
                Equipment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Venue Provides</Label>
                <Input
                  value={venueProvides}
                  onChange={(e) => setVenueProvides(e.target.value)}
                  placeholder="e.g., PA System, Monitor wedges, or 'No house gear — talent brings everything'"
                  className="h-10 bg-secondary border-border"
                />
                {venueProvides?.toLowerCase().includes('no house') && (
                  <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/50">No house gear — talent brings everything</Badge>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Talent Brings</Label>
                <div className="flex gap-2">
                  <Input
                    value={newTalentBring}
                    onChange={(e) => setNewTalentBring(e.target.value)}
                    placeholder="e.g., Instrument, Personal cables"
                    className="h-10 bg-secondary border-border"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag(newTalentBring, talentBrings, setTalentBrings, setNewTalentBring))}
                  />
                  <Button type="button" variant="outline" size="icon" className="border-border bg-secondary shrink-0" onClick={() => addTag(newTalentBring, talentBrings, setTalentBrings, setNewTalentBring)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {talentBrings.map((item) => (
                    <Badge key={item} variant="secondary" className="gap-1 bg-secondary border-border">
                      {item}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(item, talentBrings, setTalentBrings)} />
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Optional</Label>
                <div className="flex gap-2">
                  <Input
                    value={newOptional}
                    onChange={(e) => setNewOptional(e.target.value)}
                    placeholder="e.g., Backup instrument"
                    className="h-10 bg-secondary border-border"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag(newOptional, optionalItems, setOptionalItems, setNewOptional))}
                  />
                  <Button type="button" variant="outline" size="icon" className="border-border bg-secondary shrink-0" onClick={() => addTag(newOptional, optionalItems, setOptionalItems, setNewOptional)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {optionalItems.map((item) => (
                    <Badge key={item} variant="outline" className="gap-1 border-border text-muted-foreground">
                      {item}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(item, optionalItems, setOptionalItems)} />
                    </Badge>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">If equipment fails: {equipmentFailurePolicy}</p>
            </CardContent>
          </Card>

          {/* Travel */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                Travel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Included Radius</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      value={includedRadiusKm}
                      onChange={(e) => setIncludedRadiusKm(e.target.value)}
                      placeholder="25"
                      className="h-10 bg-secondary border-border"
                    />
                    <span className="text-sm text-muted-foreground">km</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Beyond Radius</Label>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">€</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={beyondRadiusRate}
                      onChange={(e) => setBeyondRadiusRate(e.target.value)}
                      placeholder="0.30"
                      className="h-10 bg-secondary border-border"
                    />
                    <span className="text-sm text-muted-foreground">/km</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Parking</Label>
                  <div className="flex items-center justify-between h-10 px-3 bg-secondary border border-border rounded-md">
                    <span className="text-sm">{parkingCovered ? 'Covered' : 'Not covered'}</span>
                    <Switch checked={parkingCovered} onCheckedChange={setParkingCovered} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Tolls</Label>
                  <div className="flex items-center justify-between h-10 px-3 bg-secondary border border-border rounded-md">
                    <span className="text-sm">{tollsCovered ? 'Covered' : 'Not covered'}</span>
                    <Switch checked={tollsCovered} onCheckedChange={setTollsCovered} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <p className="text-center text-sm text-muted-foreground">Clear terms mean fewer surprises for everyone.</p>

        {/* Submit */}
        <div className="mt-8 pb-8">
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-14 text-lg font-bold uppercase tracking-wide bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="publish-request-btn"
          >
            <Send className="h-5 w-5 mr-2" />
            {submitting ? 'Publishing...' : 'Publish Request'}
          </Button>
        </div>
      </form>
    </div>
  );
}
